import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type FC, useMemo } from 'react';
import type { FieldErrors, FieldPath, Validate } from 'react-hook-form-latest';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form-latest';

import { _isNil, _isNotEmpty, _notNil, _toNumber } from '@/littledash';

import type { ID, ISODateTime } from '@/model/Common.model';
import type { Treatment, TreatmentCalculation, TreatmentField, TreatmentMethod } from '@/model/Treatment.model';

import { useRequest } from '@/support/Hooks/request';
import { decimalPlacesValidator, nilOrGreaterThan, nilOrGreaterThanOrEqualTo, valueRequired } from '@/validators';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';
import WithAside from '@/components/UI/FormElements/WithAside';
import { NumberInput } from '@/components/UI/FormFields/Latest/Number';
import { toWorkflowDateTime } from '@/components/Workflow/Show/Workflow.utils';

import { calculateDoseFormula, evaluateCalculation, initialDoseFormDefaultState } from './AddDose.utils';

const buildConversionFormulas = (fields: Array<TreatmentField>): Record<string, string> =>
  fields.reduce<Record<string, string>>((acc, field) => {
    if (_notNil(field.unit.base_conversion_formula)) {
      acc[field.name] = field.unit.base_conversion_formula;
    }
    return acc;
  }, {});

// Protection against race condition of unregistered form values when submit occurs
const isFormReady = (fields: Array<TreatmentField>, values: DoseValues | undefined): boolean => {
  if (_isNil(values)) {
    return false;
  }
  const allFieldsRegistered = fields.every((field) => field.name in values);
  if (!allFieldsRegistered) {
    return false;
  }
  return fields
    .filter((field) => field.required)
    .every((field) => {
      const value = values[field.name as keyof DoseValues];
      return _notNil(value) && value !== '';
    });
};

interface AnimalDoseProps {
  animalId: ID;
  bodyweight?: string;
  workflowDate: string;
}

export interface DoseCreateResponse {
  data: { data: Treatment };
}

interface AddDoseFormProps extends AnimalDoseProps {
  treatment: Treatment;
  disabledSubmit?: boolean;
  disabled?: boolean;
  onSave: (response: DoseCreateResponse) => void;
  takingWeight?: boolean;
  autoFocus?: boolean;
}

export interface DoseValues {
  dose?: string | null;
  stock?: string | null;
  volume?: string | null;
  dose_volume?: string | null;
  weight_at_dosing?: string | null;
}

type Notes = string | null;

export interface FormValues {
  values: DoseValues;
  notes: Notes;
}

interface Payload extends DoseValues {
  dosed_at: ISODateTime;
  treatment_id: ID;
  notes: Notes;
}

const AddDose: FC<AddDoseFormProps> = ({
  animalId,
  takingWeight,
  bodyweight,
  treatment,
  disabledSubmit = false,
  disabled = false,
  onSave,
  workflowDate,
  autoFocus = false,
}) => {
  const conversionFormulas = useMemo(() => buildConversionFormulas(treatment.fields), [treatment]);
  const defaultValues = useMemo(() => initialDoseFormDefaultState(treatment.fields), [treatment]);
  const formMethods = useForm<FormValues>({ defaultValues });
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = formMethods;
  const {
    sendRequest: saveDose,
    requestSending: doseSaving,
    requestError,
  } = useRequest({
    params: { animalId },
    route: 'animals.dose.create',
    method: 'post',
  });

  const handlePayload = ({ notes, values }: FormValues): Payload => {
    let valuesPayload = {};
    if (_notNil(values) && _isNotEmpty(values)) {
      valuesPayload = Object.entries(values).reduce(
        (acc, [field, value]) => ({
          ...acc,
          [field]: _toNumber(value)?.toString() ?? null,
        }),
        {}
      );
    }
    return {
      dosed_at: toWorkflowDateTime(workflowDate),
      treatment_id: treatment.id,
      weight_at_dosing: takingWeight === true ? bodyweight : undefined,
      notes: notes ?? null,
      ...valuesPayload,
    };
  };

  const onSubmit = async (data: FormValues): Promise<void> => {
    if (!isFormReady(treatment.fields, data.values)) {
      return;
    }
    const response = (await saveDose(handlePayload(data))) as DoseCreateResponse;
    if (_notNil(onSave)) {
      onSave(response);
    }
  };

  const treatmentId = treatment.id.toString();

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate data-test-component="AddDose" data-test-element="form">
        {requestError !== false && <ApiErrorBanner error={requestError} />}
        {treatment.fields.map((field: TreatmentField, index) => (
          <div
            className="ph3 mb3"
            key={`${treatment.id}_${field.name}`}
            data-test-component="AddDose"
            data-test-element="dose-field-row"
          >
            <DoseField
              treatmentName={treatmentId}
              treatmentType={treatment.type}
              field={field}
              disabled={disabled}
              autoFocus={autoFocus && index === 0}
            />
            <ErrorMessage
              errors={errors}
              name={`values.${field.name}`}
              render={({ message }) => <small className="db red pt2">{message}</small>}
            />
          </div>
        ))}
        {_notNil(treatment.calculates) && (
          <div className="ph3">
            <DoseFormula
              treatmentName={treatmentId}
              bodyweight={bodyweight}
              calculation={treatment.calculates}
              conversionFormulas={conversionFormulas}
              treatmentType={treatment.type}
            />
          </div>
        )}
        <div className="mt3 ph3" data-test-component="AddDose" data-test-element="notes-row">
          <label htmlFor={`${treatmentId}-notes`}>Notes</label>
          <textarea
            id={`${treatmentId}-notes`}
            disabled={disabled}
            style={{ marginBottom: 0 }}
            data-testid={`${treatmentId}-dose-notes`}
            {...register('notes', {
              maxLength: { value: 255, message: 'Maximum of 255 characters' },
            })}
          />
          <ErrorMessage
            errors={errors}
            name="notes"
            render={({ message }) => <small className="db red pt2">{message}</small>}
          />
        </div>
        <Button
          testId={`${treatmentId}-dose-save`}
          className="mh3 mt3 dose-submit"
          submit
          disabled={doseSaving || disabledSubmit || disabled}
          tooltip={disabledSubmit ? 'A body weight is required' : ''}
        >
          Save
        </Button>
      </form>
    </FormProvider>
  );
};

interface DoseFieldProps {
  treatmentName: string;
  treatmentType: TreatmentMethod;
  field: TreatmentField;
  disabled: boolean;
  autoFocus?: boolean;
}

const DoseField: FC<DoseFieldProps> = ({ treatmentName, treatmentType, field, disabled, autoFocus = false }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();
  const fieldErrors: FieldErrors<DoseValues> | undefined = errors?.values;
  const isError = _notNil(fieldErrors?.[field.name as keyof DoseValues]);
  const allowZero = field.name === 'dose' && (treatmentType === 'custom' || treatmentType === 'dose_volume');
  const inputId = `${treatmentName}.${field.name}.value`;
  type DoseFieldValidate = Validate<unknown, FormValues>;
  return (
    <>
      {_notNil(field.label) && <label htmlFor={inputId}>{field.label}</label>}
      <Controller
        name={`values.${field.name}` as FieldPath<FormValues>}
        defaultValue=""
        rules={{
          required: field.required ? `${field.label} is required` : undefined,
          validate: {
            valueRequired: valueRequired(field.label, field.required) as DoseFieldValidate,
            decimalPlacesValidator: decimalPlacesValidator(field.label) as DoseFieldValidate,
            greaterThan: (allowZero
              ? nilOrGreaterThanOrEqualTo(field.label, 0, field.required)
              : nilOrGreaterThan(field.label, 0, field.required)) as DoseFieldValidate,
          },
        }}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WithAside
            style={{ width: 160 }}
            render={<small className="ma2 lh-title near-black bg-white">{field?.unit?.display_unit ?? ''}</small>}
          >
            <NumberInput
              id={inputId}
              className={cn({ input__error: isError })}
              onChange={onChange}
              value={value as string | number | undefined}
              step="0.01"
              style={{ marginBottom: 0 }}
              disabled={disabled}
              autoFocus={autoFocus}
            />
          </WithAside>
        )}
      />
    </>
  );
};

interface DoseFormulaProps {
  treatmentName: string;
  bodyweight?: string;
  calculation: TreatmentCalculation;
  conversionFormulas: Record<string, string>;
  treatmentType: TreatmentMethod;
}

const DoseFormula: FC<DoseFormulaProps> = ({
  treatmentName,
  bodyweight,
  calculation,
  conversionFormulas,
  treatmentType,
}) => {
  const { watch } = useFormContext<FormValues>();
  const formula = calculation?.formula ?? '';
  let formulaResult: string | null = '-';

  if (_notNil(calculation?.formula)) {
    formulaResult = calculateDoseFormula(bodyweight, watch('values'), formula, conversionFormulas, treatmentType);
    const displayConversion = calculation?.unit?.display_conversion_formula;
    if (_notNil(displayConversion)) {
      formulaResult = evaluateCalculation(displayConversion, { value: formulaResult ?? '' }, true);
    }
  }

  return (
    <div className="mt3">
      <div className="pt2" data-testid={`${treatmentName}-calculation-result`}>
        {_notNil(calculation?.label) && <label>{calculation?.label}</label>}
        {_notNil(formulaResult) && (
          <div
            className="lh-title near-black dib bg-near-white br2 pa2"
            data-testid={`${treatmentName}-calculation-result-value`}
          >
            <h3 className="f3 normal dib">
              {formulaResult !== 'Infinity' && formulaResult !== 'NaN' ? formulaResult : '-'}
            </h3>
            {_notNil(calculation?.unit?.display_unit) && (
              <small className="f5 lh-title dib pl2">{calculation?.unit.display_unit}</small>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddDose;
